import { useConfirm } from '@/features/common/ConfirmDialog/useConfirm';
import { common } from '@/i18n/dictionaries/common';
import { publicEvents } from '@/i18n/dictionaries/publicEvents';
import { useTranslation } from '@/i18n/useTranslation';
import type { StatusKind } from '@/features/common/types/StatusKind';
import { REGISTER_STATUS } from '@/features/publicsite/events/registerStatus';
import type { RegisterStatus } from '@/features/publicsite/types/events/RegisterStatus';
import type { EventRegistrationProps } from '@/features/publicsite/types/events/EventRegistrationProps';

/**
 * The tone MUD's inline message gives the line below, where it has one.
 *
 * „Înregistrarea a eșuat" came out green and bold: it shares its live region with the line that
 * reports a successful registration, and the global [role=status] rule painted every one of them
 * in the success colour. The colours are MUD's own, from .message--inline in styles/message.css.
 *
 * The glyph MUD pairs with an inline message is left off, and StatusLine is not used here: this
 * one region also carries the sign-in prompt and its link, which is neither an outcome nor a
 * failure, and splitting it would replace the live region as the visitor's attempt changes.
 */
const TONE: Partial<Record<RegisterStatus, StatusKind>> = {
  [REGISTER_STATUS.done]: 'success',
  [REGISTER_STATUS.failed]: 'error',
};

/**
 * The "Participare" section's call to action: register while open, withdraw while registered, or say
 * the event is over or off.
 *
 * `registered` and `mayCancel` are the server's answer for a signed-in visitor (GET /events/{id}/me).
 * Without the first, a participant who had already registered met a live button, and pressing it
 * again ended in the 409 the page reads as "could not be recorded"; without the second there was no
 * way back out of a registration at all (tor.md §6.11 „înscrierea, participarea”).
 */
export function EventRegistration({
  open,
  cancelled,
  registered,
  mayCancel,
  hasMedia,
  status,
  onRegister,
  onCancel,
}: EventRegistrationProps) {
  const { t } = useTranslation(publicEvents);
  const { t: tc } = useTranslation(common);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const tone = TONE[status];
  if (cancelled) {
    return <p>{t('cancelledNotice')}</p>;
  }
  if (!open) {
    return <p>{hasMedia ? t('concludedWithMedia') : `${t('concluded')}.`}</p>;
  }
  return (
    <>
      <p>{t('registrationOpen')}</p>
      {registered ? (
        mayCancel && (
          <button
            className="btn btn--secondary btn--md event-detail__register"
            type="button"
            onClick={async () => {
              const confirmed = await confirm({
                title: tc('confirmTitle'),
                body: t('cancelRegistrationConfirm'),
                confirmLabel: t('cancelRegistration'),
                cancelLabel: tc('goBack'),
                tone: 'danger',
              });
              if (confirmed) onCancel();
            }}
          >
            {t('cancelRegistration')}
          </button>
        )
      ) : (
        <button
          className="btn btn--primary btn--md event-detail__register"
          type="button"
          onClick={onRegister}
        >
          {t('register')}
        </button>
      )}
      <p
        role="status"
        className={`event-detail__status${tone ? ` message message--inline message--${tone}` : ''}`}
      >
        {registered && (status === REGISTER_STATUS.done ? t('registered') : t('alreadyRegistered'))}
        {!registered && status === REGISTER_STATUS.cancelled && t('registrationCancelled')}
        {status === REGISTER_STATUS.signin && (
          <>
            {t('registerSignIn')} <a href="/saml/authenticate/mpass">{t('registerSignInLink')}</a>
          </>
        )}
        {status === REGISTER_STATUS.failed && t('registerFailed')}
      </p>
      {confirmDialog}
    </>
  );
}
